/**
 * API服务层
 * 封装常用的API调用
 */
import { apiGet, apiPost } from '@/utils/request'
import { API_ENDPOINTS } from '@/constants'

/**
 * 域名服务
 */
export const domainService = {
  /**
   * 获取域名列表
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  getDomains(vm = null) {
    return apiGet(API_ENDPOINTS.DOMAINS, {}, { vm, showLoading: true, loadingText: '加载域名列表...' })
  },

  /**
   * 添加域名
   * @param {string} domain - 域名
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  addDomain(domain, vm = null) {
    return apiPost(
      API_ENDPOINTS.ADD_DOMAIN,
      { domain },
      {},
      { vm, showSuccess: true, successMessage: '域名添加成功', showLoading: true }
    )
  },

  /**
   * 删除域名
   * @param {number} id - 域名ID
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  deleteDomain(id, vm = null) {
    return apiPost(
      API_ENDPOINTS.DELETE_DOMAIN,
      { id },
      {},
      { vm, showSuccess: true, successMessage: '域名删除成功', showLoading: true }
    )
  },

  /**
   * 暂停/恢复域名
   * @param {number} id - 域名ID
   * @param {boolean} suspend - 是否暂停
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  suspendDomain(id, suspend, vm = null) {
    return apiPost(
      API_ENDPOINTS.SUSPEND_DOMAIN,
      { id },
      {},
      { 
        vm, 
        showSuccess: true, 
        successMessage: suspend ? '域名暂停成功' : '域名恢复成功',
        showLoading: true 
      }
    )
  }
}

/**
 * DNS记录服务
 */
export const recordService = {
  /**
   * 获取DNS记录列表
   * @param {string} domain - 域名
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  getRecords(domain, vm = null) {
    return apiGet(
      API_ENDPOINTS.RECORDS,
      { params: { domain } },
      { vm, showLoading: true, loadingText: '加载DNS记录...' }
    )
  },

  /**
   * 获取支持的记录类型
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  getRecordTypes(vm = null) {
    return apiGet(API_ENDPOINTS.RECORD_TYPES, {}, { vm })
  },

  /**
   * 添加DNS记录
   * @param {Object} recordData - 记录数据
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  addRecord(recordData, vm = null) {
    return apiPost(
      API_ENDPOINTS.ADD_RECORD,
      recordData,
      {},
      { vm, showSuccess: true, successMessage: 'DNS记录添加成功', showLoading: true }
    )
  },

  /**
   * 编辑DNS记录
   * @param {Object} recordData - 记录数据
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  editRecord(recordData, vm = null) {
    return apiPost(
      API_ENDPOINTS.EDIT_RECORD,
      recordData,
      {},
      { vm, showSuccess: true, successMessage: 'DNS记录修改成功', showLoading: true }
    )
  },

  /**
   * 删除DNS记录
   * @param {number} id - 记录ID
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  deleteRecord(id, vm = null) {
    return apiPost(
      API_ENDPOINTS.DELETE_RECORD,
      { id },
      {},
      { vm, showSuccess: true, successMessage: 'DNS记录删除成功', showLoading: true }
    )
  },

  /**
   * 启用/禁用DNS记录
   * @param {number} id - 记录ID
   * @param {boolean} disable - 是否禁用
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  disableRecord(id, disable, vm = null) {
    return apiPost(
      API_ENDPOINTS.DISABLE_RECORD,
      { id, disable },
      {},
      { 
        vm, 
        showSuccess: true, 
        successMessage: disable ? 'DNS记录已禁用' : 'DNS记录已启用',
        showLoading: true 
      }
    )
  }
}

/**
 * IP/PTR服务
 */
export const ptrService = {
  /**
   * 获取IP列表
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  getIPs(vm = null) {
    return apiGet(API_ENDPOINTS.GET_IPS, {}, { vm, showLoading: true, loadingText: '加载IP列表...' })
  },

  /**
   * 添加PTR记录
   * @param {Object} ptrData - PTR记录数据
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  addPTR(ptrData, vm = null) {
    return apiPost(
      API_ENDPOINTS.ADD_PTR,
      ptrData,
      {},
      { vm, showSuccess: true, successMessage: 'PTR记录添加成功', showLoading: true }
    )
  }
}

/**
 * DNSSEC服务
 */
export const dnssecService = {
  /**
   * 获取DNSSEC状态
   * @param {string} domain - 域名
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  getDNSSEC(domain, vm = null) {
    return apiGet(
      API_ENDPOINTS.DNSSEC,
      { params: { domain } },
      { vm, showLoading: true, loadingText: '加载DNSSEC信息...' }
    )
  },

  /**
   * 启用DNSSEC
   * @param {string} domain - 域名
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  enableDNSSEC(domain, vm = null) {
    return apiPost(
      API_ENDPOINTS.DNSSEC_ENABLE,
      { domain },
      {},
      { vm, showSuccess: true, successMessage: 'DNSSEC启用成功', showLoading: true }
    )
  },

  /**
   * 禁用DNSSEC
   * @param {string} domain - 域名
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  disableDNSSEC(domain, vm = null) {
    return apiPost(
      API_ENDPOINTS.DNSSEC_DISABLE,
      { domain },
      {},
      { vm, showSuccess: true, successMessage: 'DNSSEC禁用成功', showLoading: true }
    )
  }
}

/**
 * 操作日志服务
 */
export const logService = {
  /**
   * 获取操作日志
   * @param {Object} params - 查询参数（page, perPage等）
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  getActionLog(params = {}, vm = null) {
    return apiGet(
      API_ENDPOINTS.ACTION_LOG,
      { params },
      { vm, showLoading: true, loadingText: '加载操作日志...' }
    )
  }
}

/**
 * 认证服务
 */
export const authService = {
  /**
   * 检查token有效性
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  checkToken(vm = null) {
    return apiGet(API_ENDPOINTS.CHECK_TOKEN, {}, { vm })
  }
}

/**
 * API凭证服务
 */
export const apiCredentialService = {
  /**
   * 获取API凭证列表
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  getCredentials(vm = null) {
    return apiGet(API_ENDPOINTS.API_CREDENTIALS, {}, { vm, showLoading: true, loadingText: '加载API凭证列表...' })
  },

  /**
   * 获取API凭证详情
   * @param {number} id - 凭证ID
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  getCredential(id, vm = null) {
    return apiGet(`${API_ENDPOINTS.API_CREDENTIAL}/${id}`, {}, { vm, showLoading: true, loadingText: '加载凭证详情...' })
  },

  /**
   * 创建API凭证
   * @param {Object} credentialData - 凭证数据
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  createCredential(credentialData, vm = null) {
    return apiPost(
      API_ENDPOINTS.API_CREDENTIALS,
      credentialData,
      {},
      { vm, showSuccess: true, successMessage: 'API凭证创建成功', showLoading: true }
    )
  },

  /**
   * 更新API凭证
   * @param {number} id - 凭证ID
   * @param {Object} credentialData - 凭证数据
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  updateCredential(id, credentialData, vm = null) {
    return apiPut(
      `${API_ENDPOINTS.API_CREDENTIAL}/${id}`,
      credentialData,
      {},
      { vm, showSuccess: true, successMessage: 'API凭证更新成功', showLoading: true }
    )
  },

  /**
   * 启用/禁用API凭证
   * @param {number} id - 凭证ID
   * @param {number} status - 状态（1=启用，0=禁用）
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  toggleCredentialStatus(id, status, vm = null) {
    return apiPost(
      `${API_ENDPOINTS.API_CREDENTIAL_STATUS}/${id}/status`,
      { status },
      {},
      { 
        vm, 
        showSuccess: true, 
        successMessage: status === 1 ? 'API凭证已启用' : 'API凭证已禁用',
        showLoading: true 
      }
    )
  },

  /**
   * 删除API凭证
   * @param {number} id - 凭证ID
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  deleteCredential(id, vm = null) {
    return apiDelete(
      `${API_ENDPOINTS.API_CREDENTIAL}/${id}`,
      {},
      { vm, showSuccess: true, successMessage: 'API凭证删除成功', showLoading: true }
    )
  },

  /**
   * 获取API调用日志
   * @param {number} id - 凭证ID
   * @param {Object} params - 查询参数
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  getCredentialLogs(id, params = {}, vm = null) {
    return apiGet(
      `${API_ENDPOINTS.API_CREDENTIAL_LOGS}/${id}/logs`,
      { params },
      { vm, showLoading: true, loadingText: '加载调用日志...' }
    )
  },

  /**
   * 获取API统计信息
   * @param {number} id - 凭证ID
   * @param {Object} vm - Vue实例
   * @returns {Promise}
   */
  getCredentialStatistics(id, vm = null) {
    return apiGet(
      `${API_ENDPOINTS.API_CREDENTIAL_STATISTICS}/${id}/statistics`,
      {},
      { vm, showLoading: true, loadingText: '加载统计信息...' }
    )
  }
}

// 默认导出
export default {
  domainService,
  recordService,
  ptrService,
  dnssecService,
  logService,
  authService,
  apiCredentialService
}

